// ID → human-name resolvers for the ITSM config UI. Each degrades to the
// raw id when the underlying list hasn't loaded or the id is unknown, so
// tables never show a blank and metadata being unavailable pre-cutover is
// tolerated gracefully.

import { useDesks, useSubdesks } from "@/hooks/use-desk-migration";
import { useItsmCredentials } from "@/hooks/use-itsm-config";
import {
  useFreshserviceWorkspaces,
  useJiraProjects,
  useSnowAssignmentGroups,
} from "@/hooks/use-itsm-metadata";
import type { ItsmProviderFamily } from "@/lib/types/itsm-config";

/** Resolve a desk id to its name (falls back to the raw id). */
export function useDeskName(tenantId: string, deskId?: string | null): string {
  const { data: desks } = useDesks(tenantId);
  if (!deskId) return "";
  return desks?.find((d) => d.id === deskId)?.name ?? deskId;
}

/** Resolve a desk's subdesk id to its team name (falls back to the raw id). */
export function useSubdeskName(
  tenantId: string,
  deskId?: string | null,
  subdeskId?: string | null,
): string {
  const { data: subdesks } = useSubdesks(
    tenantId,
    subdeskId ? (deskId ?? "") : "",
  );
  if (!subdeskId) return "";
  return subdesks?.find((s) => s.id === subdeskId)?.name ?? subdeskId;
}

/** Resolve a credential id to "name (provider)" (falls back to the raw id). */
export function useCredentialName(
  tenantId: string,
  credentialId?: string | null,
): string {
  const { data: creds } = useItsmCredentials(tenantId);
  if (!credentialId) return "";
  const c = creds?.find((x) => x.id === credentialId);
  return c ? `${c.name} (${c.provider})` : credentialId;
}

/**
 * Resolve a provider_scope value to a readable label per family:
 * Jira project keys are already readable; ServiceNow assignment-group sys_ids
 * and Freshservice workspace ids are opaque, resolved via metadata. The
 * Freshservice scope is a workspace id (seeded from selected_workspaces), so
 * it must resolve against the workspace list — not the group list.
 */
export function useScopeLabel(
  family: ItsmProviderFamily,
  tenantId: string,
  credentialId: string,
  scope?: string | null,
): string {
  const jira = useJiraProjects(
    family === "jira" ? tenantId : undefined,
    credentialId,
  );
  const snow = useSnowAssignmentGroups(
    family === "servicenow" ? tenantId : undefined,
    credentialId,
  );
  const fs = useFreshserviceWorkspaces(
    family === "freshservice" ? tenantId : undefined,
    credentialId,
  );
  if (!scope) return "";
  const query = family === "jira" ? jira : family === "servicenow" ? snow : fs;
  return query.data?.options.find((o) => o.value === scope)?.label ?? scope;
}
